import {useLayoutEffect, useMemo, useRef, useState} from 'react';

import PropTypes from 'prop-types';

import {Box, Skeleton, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {RichTextContent} from '@amblique/contentful/app/components/rich-text-content';
import {Image} from '@amblique/king-living/app/contentful/models/image';
import {Video} from '@amblique/king-living/app/contentful/models/video';

const MEDIA_TYPES = new Set(['Image', 'Video']);

const CF015_RICH_CLASS = 'kl-cf015-richtext';

const CF015_RICH_SELECTORS = ['p', 'p.chakra-text', 'li', 'a', 'blockquote p'];

/** @media + !important so body copy wins over global `.amb-markup p`. */
const CF015_BODY_FONT = {
  '@media screen and (max-width: 767.98px)': {
    fontSize: '16px',
  },
  '@media screen and (min-width: 768px)': {
    fontSize: '20px',
  },
};

const CF015_RICH_MARKUP = `.rich-text-content.${CF015_RICH_CLASS}.amb-markup`;

const cf015RichBodySx = CF015_RICH_SELECTORS.reduce((acc, suffix) => {
  const block = {
    color: '#FFFFFF',
    ...CF015_BODY_FONT,
  };

  acc[`& ${CF015_RICH_MARKUP} ${suffix}`] = block;
  acc[`& > ${CF015_RICH_MARKUP} ${suffix}`] = block;

  return acc;
}, {});

const PINNED_LAYER_DESKTOP_SX = {display: {base: 'none', md: 'block'}};

function pinnedLayerMobileSx(hasDesktopPair) {
  return {display: {base: 'block', md: hasDesktopPair ? 'none' : 'block'}};
}

/**
 * Normalize Contentful `options` (object or JSON string) for merge into video props.
 */
function parseVideoOptionsFromMedia(raw) {
  if (raw == null) {
    return {};
  }

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return {...raw};
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();

    if (!trimmed) {
      return {};
    }

    try {
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        const parsed = JSON.parse(trimmed);

        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return {...parsed};
        }
      }
    } catch {
      // Invalid JSON from CMS; treat as no inline options
    }
  }

  return {};
}

/**
 * CF-015 background video: force chromeless playback so CMS options cannot re-enable controls.
 * `background` is a Vimeo embed param (ignored by other providers) that hides pause/title chrome.
 */
function getBackgroundVideoComponent(media) {
  const next = {...media, presets: 'hero'};
  const fromCms = parseVideoOptionsFromMedia(media.options);

  next.options = {
    ...fromCms,
    controls: false,
    background: true,
  };

  return next;
}

function collectScrollableItems(component) {
  const fromCollection = component?.scrollableTextCollection?.items ?? [];
  const fromCursor = component?.scrollableTextCursorCollection?.items ?? [];
  const raw = fromCollection.length ? fromCollection : fromCursor;

  return raw.filter((item) => item != null && item?.sys?.id);
}

function PinnedMediaLayer({cf015Layer, isComplete, isFetching, media, mediaLayerStyles, sx}) {
  if (!media || !MEDIA_TYPES.has(media.__typename)) {
    return null;
  }

  return (
    <Box data-kl-cf015-layer={cf015Layer} inset={0} position="absolute" sx={sx}>
      <Box {...mediaLayerStyles} aria-hidden={media.__typename === 'Video'} overflow="hidden">
        {media.__typename === 'Video' && (
          <Video
            component={getBackgroundVideoComponent(media)}
            isComplete={isComplete}
            isFetching={isFetching}
            fillStickyBand
          />
        )}
        {media.__typename === 'Image' && (
          <Image component={media} isComplete={isComplete} isFetching={isFetching} />
        )}
      </Box>
    </Box>
  );
}

PinnedMediaLayer.propTypes = {
  cf015Layer: PropTypes.oneOf(['desktop', 'mobile']).isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  media: PropTypes.shape({
    __typename: PropTypes.string,
  }).isRequired,
  mediaLayerStyles: PropTypes.object,
  sx: PropTypes.object,
};

function readHeaderHeightPx() {
  if (typeof document === 'undefined') {
    return 0;
  }

  const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
  const n = Number.parseFloat(raw);

  return Number.isFinite(n) ? n : 0;
}

const SCROLL_FADE_EDGE_RATIO = 0.4;
const SCROLL_FADE_EDGE_MAX_PX = 320;

function opacityForCenterY(cy, bandTop, bandBottom) {
  const bandH = Math.max(1, bandBottom - bandTop);
  const fade = Math.min(bandH * SCROLL_FADE_EDGE_RATIO, SCROLL_FADE_EDGE_MAX_PX);

  if (cy <= bandTop) {
    return 0;
  }

  if (cy < bandTop + fade) {
    return Math.min(1, Math.max(0, (cy - bandTop) / fade));
  }

  if (cy >= bandBottom) {
    return 0;
  }

  if (cy > bandBottom - fade) {
    return Math.min(1, Math.max(0, (bandBottom - cy) / fade));
  }

  return 1;
}

function ScrollFadeStep({children, prefersReducedMotion, styles}) {
  const ref = useRef(null);
  const layerRef = useRef(null);

  useLayoutEffect(() => {
    if (prefersReducedMotion) {
      return undefined;
    }

    const el = ref.current;
    const layer = layerRef.current;

    if (!el || !layer) {
      return undefined;
    }

    let raf = 0;

    const apply = () => {
      const rect = el.getBoundingClientRect();
      const bandTop = readHeaderHeightPx();
      const bandBottom = typeof window !== 'undefined' ? window.innerHeight : 0;
      const cy = (rect.top + rect.bottom) / 2;

      layer.style.opacity = String(opacityForCenterY(cy, bandTop, bandBottom));
    };

    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };

    layer.style.willChange = 'opacity';
    apply();
    window.addEventListener('scroll', update, {passive: true});
    window.addEventListener('resize', update);
    const ro = new ResizeObserver(update);

    ro.observe(el);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro.disconnect();
      layer.style.willChange = '';
    };
  }, [prefersReducedMotion]);

  if (prefersReducedMotion) {
    return (
      <Box ref={ref} {...styles.step}>
        <Box {...styles.stepInner}>{children}</Box>
      </Box>
    );
  }

  return (
    <Box ref={ref} {...styles.step} position="relative">
      <Box
        ref={layerRef}
        alignItems="center"
        bottom={0}
        display="flex"
        justifyContent="center"
        left={0}
        position="absolute"
        right={0}
        style={{opacity: 1}}
        top={0}
      >
        <Box {...styles.stepInner}>{children}</Box>
      </Box>
    </Box>
  );
}

ScrollFadeStep.propTypes = {
  children: PropTypes.node,
  prefersReducedMotion: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};

/**
 * CF-015: sticky background media with scroll-driven text steps and cross-fade.
 */
export const MediaWithScrollableText = ({component, isComplete = true, isFetching = false, ...props}) => {
  const styles = useMultiStyleConfig('contentful/media-with-scrollable-text', {
    component,
    isComplete,
    isFetching,
  });

  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useLayoutEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReducedMotion(Boolean(mq.matches));

    update();
    mq.addEventListener('change', update);

    return () => mq.removeEventListener('change', update);
  }, []);

  const {backgroundMedia, backgroundMediaMobile} = component ?? {};
  const items = useMemo(() => collectScrollableItems(component), [component]);

  // `backgroundMedia` = desktop layer; `backgroundMediaMobile` = mobile (falls back to desktop if unset).
  const desktopMedia = backgroundMedia && MEDIA_TYPES.has(backgroundMedia.__typename) ? backgroundMedia : null;
  const mobileMedia =
    backgroundMediaMobile && MEDIA_TYPES.has(backgroundMediaMobile.__typename) ?
      backgroundMediaMobile :
      desktopMedia;

  const hasBackground = Boolean(desktopMedia || mobileMedia);
  const exitSteps = items.length > 0 ? 1 : 0;
  const textSteps = items.length;
  const isLoadingShell = Boolean(isFetching && !isComplete);

  if (!hasBackground && textSteps === 0 && !isLoadingShell) {
    return null;
  }

  if (isLoadingShell && !hasBackground && textSteps === 0) {
    return (
      <Box as="section" {...styles.section} {...props}>
        <Box {...styles.stickyViewport}>
          <Skeleton endColor="gray.500" height="100%" position="absolute" startColor="gray.700" width="100%" />
        </Box>
      </Box>
    );
  }

  return (
    <Box as="section" {...styles.section} {...props}>
      <Box {...styles.stickyViewport}>
        {desktopMedia && (
          <PinnedMediaLayer
            cf015Layer="desktop"
            isComplete={isComplete}
            isFetching={isFetching}
            media={desktopMedia}
            mediaLayerStyles={styles.mediaLayer}
            sx={PINNED_LAYER_DESKTOP_SX}
          />
        )}
        {mobileMedia && (
          <PinnedMediaLayer
            cf015Layer="mobile"
            isComplete={isComplete}
            isFetching={isFetching}
            media={mobileMedia}
            mediaLayerStyles={styles.mediaLayer}
            sx={pinnedLayerMobileSx(Boolean(desktopMedia))}
          />
        )}
      </Box>

      {(textSteps > 0 || exitSteps > 0) && (
        <Box {...styles.textStack}>
          {items.map((entry) => (
            <ScrollFadeStep key={entry.sys.id} prefersReducedMotion={prefersReducedMotion} styles={styles}>
              {entry.heading && (
                <Text as="h3" {...styles.heading}>
                  {entry.heading}
                </Text>
              )}
              {entry.body?.json && (
                <Box
                  {...styles.body}
                  sx={{
                    ...(styles.body?.sx ?? {}),
                    ...cf015RichBodySx,
                  }}
                >
                  <RichTextContent className={CF015_RICH_CLASS} content={entry.body} />
                </Box>
              )}
            </ScrollFadeStep>
          ))}
          {exitSteps > 0 && (
            <Box opacity={0} {...styles.step} aria-hidden />
          )}
        </Box>
      )}
    </Box>
  );
};

MediaWithScrollableText.displayName = 'MediaWithScrollableText';
MediaWithScrollableText.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
